import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Landmark, Briefcase, TrendingUp, ArrowRight } from 'lucide-react';
const practicesData = [{
  slug: 'tax-and-accounting',
  title: 'Tax & Accounting',
  icon: <Landmark size={48} data-id="element-511" data-mp="k485ztyd4naq" />,
  color: 'bg-brand-navy',
  heroImg: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80',
  description: 'Precision-driven tax planning and accounting services for corporate and private wealth clients. We deliver institutional-quality solutions tailored to the unique needs of entrepreneurs, investors, and corporate leaders.',
  services: [{
    name: 'Individual & Business Tax Preparation',
    desc: 'Comprehensive tax compliance and strategic corporate filing for individuals, SMEs, and large entities. We ensure accuracy and adherence to the latest regulatory requirements.'
  }, {
    name: 'Tax Planning',
    desc: 'Proactive strategies to optimize tax positions and preserve wealth. Our forward-looking approach helps mitigate liabilities and maximize returns.'
  }, {
    name: 'Bookkeeping & Financial Statements',
    desc: 'Meticulous financial record-keeping and preparation of institutional-quality reports and audits. We provide clear visibility into your financial health.'
  }, {
    name: 'Payroll Services',
    desc: 'Comprehensive and compliant payroll management for your organization. Streamline your operations with our reliable and accurate payroll solutions.'
  }, {
    name: 'Outsourced CFO Services',
    desc: 'Strategic financial leadership for growing businesses. Gain access to executive-level financial expertise without the overhead of a full-time CFO.'
  }]
}, {
  slug: 'strategic-advisory',
  title: 'Strategic Advisory',
  icon: <Briefcase size={48} data-id="element-512" data-mp="bpxpvdaffq5v" />,
  color: 'bg-brand-emerald',
  heroImg: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80',
  description: 'Comprehensive business strategy, market entry, and operational optimization for sustainable growth. We partner with you to navigate complex challenges and seize high-value opportunities.',
  services: [{
    name: 'Buy-Side Advisory & Deal Screening',
    desc: 'Expert guidance through the acquisition process, combining rigorous analysis and due diligence from identification to closing. We help you make informed investment decisions.'
  }, {
    name: 'Business Acquisitions',
    desc: 'Strategic support for expanding your portfolio through targeted acquisitions. We identify synergies and structure deals that align with your long-term objectives.'
  }, {
    name: 'International Expansion & Strategic Partnerships',
    desc: 'Navigating the complexities of entering new global markets and facilitating high-value alliances. We provide the local insights and global network needed for successful expansion.'
  }, {
    name: 'Business Process Improvement',
    desc: 'Optimizing operations for maximum efficiency and profitability. We analyze your workflows to eliminate bottlenecks and enhance overall performance.'
  }, {
    name: 'Portfolio Company Advisory',
    desc: 'Strategic guidance and operational support to maximize the value of portfolio companies. We work closely with management teams to drive growth and operational excellence.'
  }]
}, {
  slug: 'consilium-capital',
  title: 'Consilium Capital',
  icon: <TrendingUp size={48} data-id="element-513" data-mp="f4mlco0amt4g" />,
  color: 'bg-brand-gold',
  heroImg: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80',
  description: 'Navigating complex capital structures, debt advisory, and equity raising for ambitious enterprises. We provide the financial architecture needed to fuel your strategic vision.',
  services: [{
    name: 'Growth Capital',
    desc: 'Tailored funding solutions to fuel business expansion and innovation. We connect you with the right capital partners to accelerate your growth trajectory.'
  }, {
    name: 'Transformation Capital',
    desc: 'Strategic investment to drive organizational change and revitalization. We provide the resources needed to execute major strategic pivots and operational overhauls.'
  }, {
    name: 'Investment Strategy',
    desc: 'Developing robust frameworks for long-term capital appreciation. We align your investment approach with your broader strategic and financial goals.'
  }]
}];
export const ServiceDetail = () => {
  const {
    slug
  } = useParams();
  const practice = practicesData.find(p => p.slug === slug);
  if (!practice) {
    return <Navigate to="/services" data-id="element-514" data-mp="j9ox4zoj4uym" />;
  }
  return <main className="pt-20 bg-brand-warm-white min-h-screen" data-id="element-515" data-mp="j8ni6k4e7obm">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-end pb-20" data-id="element-516" data-mp="2uvqu7d2cnkug">
        <div className="absolute inset-0 z-0" data-id="element-517" data-mp="2zoagcyf3roav">
          <img src={practice.heroImg} alt={practice.title} className="w-full h-full object-cover" data-id="element-518" data-mp="2zp3fpebqfbiz" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/80 to-transparent" data-id="element-519" data-mp="2pwnu871884tc" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 w-full flex flex-col md:flex-row md:items-end justify-between gap-8" data-id="element-520" data-mp="3dnjjoxi7mnxf">
          <div data-id="element-521" data-mp="3ijqq0reiq1fw">
            <Link to="/services" className="inline-flex items-center gap-2 text-white/70 hover:text-brand-gold transition-colors mb-8 text-sm font-bold tracking-widest uppercase" data-id="element-522" data-mp="34mlon4pr1qqz">
              <ArrowLeft size={16} data-id="element-523" data-mp="1t656v4qq2y0y" /> Back to Services
            </Link>
            
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-center gap-6" data-id="element-524" data-mp="1y6sy9riv5gvp">
              <div className={`p-4 rounded-2xl text-white ${practice.color} hidden md:block`} data-id="element-525" data-mp="3uwchebx778oq">
                {practice.icon}
              </div>
              <h1 className="text-4xl md:text-6xl font-display text-white leading-tight" data-id="element-526" data-mp="3q4b3ymoanbx4">
                {practice.title}
              </h1>
            </motion.div>
          </div>
          
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} data-id="element-527" data-mp="3h5z9g9kb3tze">
            <Link to="/contact" className="bg-brand-gold hover:bg-white hover:text-brand-navy text-white px-8 py-4 text-sm font-bold uppercase tracking-widest transition-all inline-block whitespace-nowrap" data-id="element-528" data-mp="376i1tmlhv60s">
              Request Consultation
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24" data-id="element-529" data-mp="11byygc2nob8t">
        <div className="max-w-7xl mx-auto px-6" data-id="element-530" data-mp="2jc808jrt3krh">
          <div className="max-w-3xl mb-20" data-id="element-531" data-mp="2j9c2avfykw31">
            <h2 className="text-3xl font-display text-brand-navy mb-6" data-id="element-532" data-mp="2o9zsxg09zawq">Overview</h2>
            <p className="text-xl text-brand-charcoal/70 leading-relaxed" data-id="element-533" data-mp="2t1ht41uh57gn">
              {practice.description}
            </p>
          </div>

          <h2 className="text-3xl font-display text-brand-navy mb-12" data-id="element-534" data-mp="2x1jm7mm58skb">Detailed Offerings</h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-535" data-mp="20dvnyp5lluro">
            {practice.services.map((service, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white p-10 rounded-3xl border border-brand-navy/5 shadow-sm hover:shadow-xl hover:border-brand-gold/30 transition-all duration-500 group" data-id="element-536" data-mp="25c6wmwk5pz8b" data-mpi={idx}>
                <div className="text-brand-gold mb-6 bg-brand-gold/10 w-12 h-12 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-500" data-id="element-537" data-mp="3ptbndctq024d">
                  <CheckCircle2 size={24} data-id="element-538" data-mp="2wdr0qqpnn9cz" />
                </div>
                <h3 className="text-2xl font-display text-brand-navy mb-4" data-id="element-539" data-mp="3a8a5h8t2moyg">
                  {service.name}
                </h3>
                <p className="text-brand-charcoal/70 leading-relaxed" data-id="element-540" data-mp="35f8moppqu59l">
                  {service.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-navy text-white overflow-hidden" data-id="element-541" data-mp="3t64bddywkkcm">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto px-6 text-center" data-id="element-542" data-mp="3t40az607q3h3">
          <h2 className="text-4xl md:text-5xl font-display mb-8" data-id="element-543" data-mp="1uqhwiyviv2y4">
            Ready to discuss your <br data-id="element-544" data-mp="1usuo83r30rod" />
            <span className="italic text-brand-gold" data-id="element-545" data-mp="1kmtdfjndlpm6">strategic needs?</span>
          </h2>
          <p className="text-white/60 text-lg mb-12" data-id="element-546" data-mp="1oo54ltlweiwh">
            Our partners are available for a strategic consultation to discuss
            how our {practice.title} services can drive your growth.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-3 bg-brand-gold text-white px-10 py-5 text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-brand-navy transition-all" data-id="element-547" data-mp="1fvbbas9m9izm">
            Schedule a Consultation <ArrowRight size={18} data-id="element-548" data-mp="15vkj4x8j3g2p" />
          </Link>
        </motion.div>
      </section>
    </main>;
};